import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { LoadingOutlined, PauseOutlined, CaretRightOutlined, RetweetOutlined } from '@ant-design/icons';
import { Button, Slider } from 'antd';
import styled from 'styled-components';
import VolumeInput from './audio-volume-input.component';
import { styleVariables } from '../../style';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var StyledContainer = styled.div.withConfig({
  displayName: "audio-playercomponent__StyledContainer",
  componentId: "sc-8avpdq-0"
})(["display:flex;align-items:center;padding:10px 14px;height:50px;background-color:", ";border-radius:8px;"], styleVariables.colors.gray3);
var StyledAudio = styled.audio.withConfig({
  displayName: "audio-playercomponent__StyledAudio",
  componentId: "sc-8avpdq-1"
})(["pointer-events:", ";opacity:", ";width:100%;"], function (_ref) {
  var $disabled = _ref.$disabled;
  return $disabled ? 'none' : 'auto';
}, function (_ref2) {
  var $disabled = _ref2.$disabled;
  return $disabled ? 0.6 : 1;
});
var StyledDuration = styled.div.withConfig({
  displayName: "audio-playercomponent__StyledDuration",
  componentId: "sc-8avpdq-2"
})(["margin-right:14px;"]);
var StyledProgressBar = styled(Slider).withConfig({
  displayName: "audio-playercomponent__StyledProgressBar",
  componentId: "sc-8avpdq-3"
})(["width:100%;flex:1;margin-right:14px;"]);
var StyledPauseOutlined = styled(PauseOutlined).withConfig({
  displayName: "audio-playercomponent__StyledPauseOutlined",
  componentId: "sc-8avpdq-4"
})(["font-size:24px;margin-right:14px;"]);
var StyledCaretRightOutlined = styled(CaretRightOutlined).withConfig({
  displayName: "audio-playercomponent__StyledCaretRightOutlined",
  componentId: "sc-8avpdq-5"
})(["font-size:24px;margin-right:14px;"]);
var StyledReplayButton = styled.div.withConfig({
  displayName: "audio-playercomponent__StyledReplayButton",
  componentId: "sc-8avpdq-6"
})(["margin-right:14px;position:relative;cursor:pointer;"]);
var StyledRetweetOutlined = styled(RetweetOutlined).withConfig({
  displayName: "audio-playercomponent__StyledRetweetOutlined",
  componentId: "sc-8avpdq-7"
})(["font-size:24px;"]);
var StyledReplayCount = styled.div.withConfig({
  displayName: "audio-playercomponent__StyledReplayCount",
  componentId: "sc-8avpdq-8"
})(["position:absolute;right:-3px;top:-3px;padding:1px;width:15px;height:15px;display:flex;justify-content:center;align-items:center;border-radius:50%;font-size:10px;color:#fff;background-color:#000;"]);
export var AudioPlayer = function AudioPlayer(_ref3) {
  var source = _ref3.source,
      onPlay = _ref3.onPlay,
      onReplay = _ref3.onReplay,
      _ref3$numberOfReplays = _ref3.numberOfReplays,
      numberOfReplays = _ref3$numberOfReplays === void 0 ? 0 : _ref3$numberOfReplays,
      isAppliedRule = _ref3.isAppliedRule,
      _ref3$audioPlayerConf = _ref3.audioPlayerConfig,
      audioPlayerConfig = _ref3$audioPlayerConf === void 0 ? {} : _ref3$audioPlayerConf,
      _ref3$hasPlayed = _ref3.hasPlayed,
      hasPlayed = _ref3$hasPlayed === void 0 ? false : _ref3$hasPlayed,
      _ref3$disabled = _ref3.disabled,
      disabled = _ref3$disabled === void 0 ? false : _ref3$disabled;
  var isAllowedToPause = audioPlayerConfig.isAllowedToPause,
      isAllowedToReplay = audioPlayerConfig.isAllowedToReplay,
      _audioPlayerConfig$is = audioPlayerConfig.isAllowedToSeek,
      isAllowedToSeek = _audioPlayerConfig$is === void 0 ? true : _audioPlayerConfig$is,
      isAutoPlay = audioPlayerConfig.isAutoPlay;
  var audioRef = useRef(null);

  var _useState = useState(false),
      isPlaying = _useState[0],
      setIsPlaying = _useState[1];

  var _useState2 = useState(0),
      duration = _useState2[0],
      setDuration = _useState2[1];

  var _useState3 = useState(false),
      isReady = _useState3[0],
      setIsReady = _useState3[1];

  var _useState4 = useState(0),
      currentTime = _useState4[0],
      setCurrentTime = _useState4[1];

  var _useState5 = useState(0.3),
      volume = _useState5[0],
      setVolume = _useState5[1];

  var _useState6 = useState(0),
      progress = _useState6[0],
      setProgress = _useState6[1];

  var _useState7 = useState(0),
      replayCount = _useState7[0],
      setReplayCount = _useState7[1]; // handlers


  var handleVolumeChange = function handleVolumeChange(volumeValue) {
    if (!audioRef.current) return;
    audioRef.current.volume = volumeValue;
    setVolume(volumeValue);
  };

  var toggleMuteUnmute = function toggleMuteUnmute() {
    if (!audioRef.current) return;

    if (audioRef.current.volume !== 0) {
      audioRef.current.volume = 0;
      setVolume(0);
    } else {
      audioRef.current.volume = volume;
    }
  };

  var handleTimeUpdate = function handleTimeUpdate() {
    var audio = audioRef.current;
    if (!audio) return;
    setProgress(audio.currentTime / audio.duration * 100);
    setCurrentTime(audio.currentTime);
  };

  var handleReplay = function handleReplay() {
    if (!isReady || !(audioRef !== null && audioRef !== void 0 && audioRef.current)) return;
    if (!isAllowedToReplay || numberOfReplays === 0) return;
    var audio = audioRef.current;
    if (audio.currentTime < duration && !hasPlayed) return;
    if (!audio.paused) return;
    audio.currentTime = 0;
    audio.play();
    setReplayCount(function (prev) {
      return prev + 1;
    });
    setIsPlaying(true);
    if (onReplay) onReplay();
  };

  var togglePlayPause = useCallback(function () {
    if (!(audioRef !== null && audioRef !== void 0 && audioRef.current)) return;
    var audio = audioRef.current;
    var isStarted = audio.currentTime === 0;
    var isEnded = audio.ended;
    if (numberOfReplays === 0) return;

    if (audio.paused) {
      audio.play();
      setIsPlaying(true);

      if (isEnded || isStarted) {
        onPlay === null || onPlay === void 0 ? void 0 : onPlay();
      }
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, [numberOfReplays]); // render

  var renderPlayButton = function renderPlayButton() {
    var _audioRef$current, _audioRef$current2;

    var isStarted = ((_audioRef$current = audioRef.current) === null || _audioRef$current === void 0 ? void 0 : _audioRef$current.currentTime) === 0;
    var isEnded = (_audioRef$current2 = audioRef.current) === null || _audioRef$current2 === void 0 ? void 0 : _audioRef$current2.ended;
    if (isPlaying && isAllowedToPause === false) return;

    if (isAllowedToPause === false && !isStarted && !isEnded) {
      return;
    }

    if (!isReady) {
      return /*#__PURE__*/_jsx(Button, {
        disabled: true,
        children: /*#__PURE__*/_jsx(LoadingOutlined, {})
      });
    }

    if (isPlaying) {
      return /*#__PURE__*/_jsx(StyledPauseOutlined, {
        onClick: togglePlayPause
      });
    }

    return /*#__PURE__*/_jsx(StyledCaretRightOutlined, {
      onClick: togglePlayPause
    });
  };

  var renderTime = function renderTime(time) {
    var momentDuration = moment.duration(time, 'seconds');
    return "".concat(momentDuration.minutes().toString().padStart(2, '0'), ":").concat(momentDuration.seconds().toString().padStart(2, '0'));
  };

  var renderReplayButton = function renderReplayButton() {
    if (!isAllowedToReplay || numberOfReplays === 0) return;
    return /*#__PURE__*/_jsxs(StyledReplayButton, {
      onClick: handleReplay,
      children: [/*#__PURE__*/_jsx(StyledRetweetOutlined, {
        disabled: numberOfReplays === 0
      }), /*#__PURE__*/_jsx(StyledReplayCount, {
        children: numberOfReplays
      })]
    });
  };

  var formatProgress = function formatProgress(value) {
    var currentTime = value / 100 * duration;
    return renderTime(currentTime);
  };

  useEffect(function () {
    if (isAutoPlay && isReady && audioRef.current) {
      togglePlayPause();
    }
  }, [isAutoPlay, isReady]);

  if (!isAppliedRule) {
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(StyledAudio, {
        controls: true,
        $disabled: disabled,
        children: /*#__PURE__*/_jsx("source", {
          src: source
        })
      })
    });
  }

  return /*#__PURE__*/_jsxs(StyledContainer, {
    children: [/*#__PURE__*/_jsx(StyledAudio, {
      ref: audioRef,
      onDurationChange: function onDurationChange(e) {
        return setDuration(e.currentTarget.duration);
      },
      onCanPlay: function onCanPlay(e) {
        setIsReady(true);
      },
      onTimeUpdate: handleTimeUpdate,
      onEnded: function onEnded() {
        return setIsPlaying(false);
      },
      $disabled: disabled,
      children: /*#__PURE__*/_jsx("source", {
        src: source
      })
    }), renderPlayButton(), /*#__PURE__*/_jsxs(StyledDuration, {
      children: [renderTime(currentTime), " / ", renderTime(duration)]
    }), /*#__PURE__*/_jsx(StyledProgressBar, {
      disabled: !isAllowedToSeek,
      value: progress,
      tooltip: {
        formatter: formatProgress
      },
      trackStyle: {
        backgroundColor: '#000'
      },
      handleStyle: {
        borderColor: '#000'
      }
    }), renderReplayButton(), /*#__PURE__*/_jsx(VolumeInput, {
      volume: volume,
      onVolumeChange: handleVolumeChange,
      onToggleMute: toggleMuteUnmute
    })]
  });
};